/**
 * Admin: Auto-Compiler Proxy — Alert configuration + recent alert audit log.
 *
 * Three panels:
 *   1. Live config (threshold % · enabled · provider filter · webhook override)
 *   2. Scheduler status (running? next tick? interval?)
 *   3. Recent alerts table (last 50 dispatched Slack alerts)
 *
 * Plus a "Send test alert" button that fires a synthetic 42 % burst on
 * groq/llama-4-70b end-to-end.
 *
 * Admin-only — backend gates via require_role(["admin"]).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings, Bell, Activity, Clock, Send, CheckCircle2, AlertTriangle,
  RefreshCw, TrendingDown, ArrowRight, Lock, Sparkles,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ADMIN = `${API}/api/engine27/admin`;

const PROVIDERS = [
  'openai', 'anthropic', 'google', 'groq', 'together', 'fireworks',
  'deepinfra', 'anyscale', 'perplexity', 'mistral', 'cohere', 'bedrock',
];

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function AdminEngine27() {
  const [config, setConfig] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [ingestion, setIngestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toast, setToast] = useState(null);
  const [scope, setScope] = useState('org'); // 'org' | 'all' (Pro-only)

  // Local form state
  const [threshold, setThreshold] = useState(35);
  const [enabled, setEnabled] = useState(true);
  const [providerFilter, setProviderFilter] = useState([]);
  const [webhookUrl, setWebhookUrl] = useState('');

  const isPro = config?.writable === true;
  const tier = config?.plan_tier || 'free';

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, schedRes, alertsRes, ingestionRes] = await Promise.all([
        fetch(`${ADMIN}/alert-config`, { headers: authHeaders() }),
        fetch(`${ADMIN}/scheduler-status`, { headers: authHeaders() }),
        fetch(`${ADMIN}/alerts?limit=50&scope=${scope}`, { headers: authHeaders() }),
        fetch(`${ADMIN}/ingestion-status`, { headers: authHeaders() }),
      ]);
      if (cfgRes.ok) {
        const cfg = await cfgRes.json();
        setConfig(cfg);
        setThreshold(cfg.threshold_pct);
        setEnabled(cfg.enabled);
        setProviderFilter(cfg.providers_filter || []);
        setWebhookUrl(cfg.webhook_url || '');
      }
      if (schedRes.ok) setScheduler(await schedRes.json());
      if (alertsRes.ok) setAlerts((await alertsRes.json()).alerts || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // When the requester flips between org/all scope, just re-pull alerts.
  useEffect(() => {
    if (!loading) refresh();
  }, [scope, loading, refresh]);

  const showToast = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${ADMIN}/alert-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          threshold_pct: Number(threshold),
          enabled,
          providers_filter: providerFilter,
          webhook_url: webhookUrl,
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      const cfg = await res.json();
      setConfig(cfg);
      showToast('ok', 'Alert config saved — takes effect on next tick');
    } catch (e) {
      showToast('error', e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API}/api/engine27/alert-test`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const body = await res.json();
      if (body.ok) {
        showToast('ok', body.demo_mode
          ? 'Test fired (demo mode — saved to demo_notifications)'
          : 'Test alert sent to Slack ✓');
        // Reload alerts after a moment so it shows up
        setTimeout(refresh, 800);
      } else {
        showToast('error', 'Test alert helper returned ok=false');
      }
    } catch (e) {
      showToast('error', e.message);
    } finally {
      setTesting(false);
    }
  };

  const toggleProvider = (id) => {
    setProviderFilter((cur) =>
      cur.includes(id) ? cur.filter((p) => p !== id) : [...cur, id]
    );
  };

  const nextRun = useMemo(() => {
    if (!scheduler?.next_run_at) return '—';
    const next = new Date(scheduler.next_run_at);
    const diff = Math.max(0, (next.getTime() - Date.now()) / 1000);
    return diff < 60 ? `in ${Math.round(diff)}s` : new Date(scheduler.next_run_at).toLocaleTimeString();
  }, [scheduler]);

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="admin-engine27-page">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 px-4 py-3 rounded-lg shadow-xl border flex items-center gap-2 ${
          toast.kind === 'ok'
            ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200'
            : 'bg-rose-500/15 border-rose-500/40 text-rose-200'
        }`} data-testid="admin-engine27-toast">
          {toast.kind === 'ok' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          <span className="text-sm">{toast.msg}</span>
        </div>
      )}

      {/* Header */}
      <header className="border-b border-white/5 px-4 py-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-emerald-300">Admin · Auto-Compiler Proxy</div>
            <h1 className="text-2xl sm:text-3xl font-bold mt-1">Real-Time Token Arbitrage · Alert Console</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              className="px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-sm flex items-center gap-2"
              data-testid="admin-engine27-refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <Link
              to="/engine-27"
              className="px-3 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-semibold flex items-center gap-2"
              data-testid="admin-engine27-public-link"
            >
              Public dashboard <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 grid gap-6 lg:grid-cols-3">
        {/* TIER BANNER */}
        {!loading && (
          <div className={`lg:col-span-3 rounded-2xl p-4 border flex items-center gap-3 ${
            isPro
              ? 'bg-emerald-500/5 border-emerald-500/20'
              : 'bg-amber-500/5 border-amber-500/30'
          }`} data-testid="admin-engine27-tier-banner">
            {isPro ? (
              <>
                <Sparkles className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-emerald-300">
                    Pro tier · per-org alert config unlocked
                  </div>
                  <div className="text-xs text-slate-400">
                    Org <code className="text-emerald-300 bg-slate-900 px-1 py-0.5 rounded">{config?.org_id}</code> · your custom threshold, filter, and webhook URL only affect this org.
                  </div>
                </div>
                <span className="px-2 py-1 rounded text-[10px] font-bold tracking-wider uppercase bg-emerald-500 text-slate-950">
                  {tier}
                </span>
              </>
            ) : (
              <>
                <Lock className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-amber-300">
                    Free tier · read-only
                  </div>
                  <div className="text-xs text-slate-400">
                    You see the platform default config. <Link to="/pricing" className="text-emerald-300 underline">Upgrade to Pro</Link> to customize threshold, provider filter, and webhook URL for your org only.
                  </div>
                </div>
                <Link
                  to="/pricing"
                  data-testid="admin-engine27-upgrade-cta"
                  className="px-3 py-1.5 rounded bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold uppercase tracking-wider"
                >
                  Upgrade
                </Link>
              </>
            )}
          </div>
        )}

        {/* CONFIG PANEL */}
        <section className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:p-6" data-testid="admin-engine27-config">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold flex items-center gap-2"><Settings className="w-4 h-4 text-emerald-400" /> Alert configuration</h2>
            <span className={`text-xs font-mono px-2 py-0.5 rounded ${config?.webhook_configured ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>
              {config?.webhook_configured ? 'webhook configured' : 'demo mode'}
            </span>
          </div>

          {/* Master switch */}
          <label className="mt-5 flex items-center gap-3 cursor-pointer group">
            <div className={`relative w-10 h-6 rounded-full transition ${enabled ? 'bg-emerald-500' : 'bg-white/10'}`}>
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => isPro && setEnabled(e.target.checked)}
                disabled={!isPro}
                className="sr-only"
                data-testid="admin-engine27-enabled"
              />
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition ${enabled ? 'translate-x-4' : ''}`} />
            </div>
            <div>
              <div className="text-sm font-semibold">Slack alerts enabled</div>
              <div className="text-xs text-slate-400">Turning off disables all burst notifications globally</div>
            </div>
          </label>

          {/* Threshold slider */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="threshold" className="text-sm font-semibold">Burst threshold</label>
              <span className="text-2xl font-extrabold tabular-nums text-emerald-300" data-testid="admin-engine27-threshold-value">{threshold}%</span>
            </div>
            <input
              id="threshold"
              type="range"
              min="10"
              max="60"
              step="1"
              value={threshold}
              onChange={(e) => isPro && setThreshold(Number(e.target.value))}
              disabled={!isPro}
              className="w-full h-2 rounded-lg bg-white/10 appearance-none cursor-pointer accent-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="admin-engine27-threshold-slider"
            />
            <div className="flex justify-between text-[10px] text-slate-500 mt-1">
              <span>10%</span><span>25%</span><span>40%</span><span>60%</span>
            </div>
            <p className="text-xs text-slate-400 mt-3 leading-relaxed">
              Only fire alerts when a model&apos;s price drops by this percentage or more.
              Lower threshold = more alerts (noisier). Higher = only the dramatic ones.
            </p>
          </div>

          {/* Provider filter */}
          <div className="mt-6">
            <div className="text-sm font-semibold">Provider filter</div>
            <p className="text-xs text-slate-400 mb-3">
              Empty = alert on all providers. Otherwise, only alert when the selected ones drop.
            </p>
            <div className="flex flex-wrap gap-2">
              {PROVIDERS.map((p) => (
                <button
                  key={p}
                  onClick={() => isPro && toggleProvider(p)}
                  disabled={!isPro}
                  type="button"
                  data-testid={`admin-engine27-provider-${p}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-mono border transition disabled:opacity-50 disabled:cursor-not-allowed ${
                    providerFilter.includes(p)
                      ? 'bg-emerald-500 border-emerald-400 text-slate-950 font-bold'
                      : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Webhook override */}
          <div className="mt-6">
            <label htmlFor="webhook" className="text-sm font-semibold">Webhook URL override</label>
            <p className="text-xs text-slate-400 mb-2">
              Leave empty to use the default <code className="text-emerald-300">SLACK_WEBHOOK_URL</code>. Set this to route Engine-27 alerts to a dedicated channel.
            </p>
            <input
              id="webhook"
              type="password"
              value={webhookUrl}
              onChange={(e) => isPro && setWebhookUrl(e.target.value)}
              disabled={!isPro}
              placeholder="https://hooks.slack.com/services/T.../B.../..."
              className="w-full px-3 py-2 rounded-md bg-slate-900 border border-white/10 text-sm font-mono disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="admin-engine27-webhook"
            />
          </div>

          {/* Actions */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving || !isPro}
              className="px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 font-bold text-sm"
              data-testid="admin-engine27-save"
            >
              {saving ? 'Saving…' : isPro ? 'Save configuration' : 'Save (Pro only)'}
            </button>
            <button
              onClick={handleTest}
              disabled={testing}
              className="px-4 py-2 rounded-md border border-white/15 hover:border-white/30 disabled:opacity-50 text-white font-semibold text-sm flex items-center gap-2"
              data-testid="admin-engine27-test"
            >
              <Send className="w-3.5 h-3.5" /> {testing ? 'Firing…' : 'Send test alert'}
            </button>
            <span className="text-xs text-slate-500">Changes take effect on the next tick (no restart needed).</span>
          </div>
        </section>

        {/* SCHEDULER STATUS */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="admin-engine27-scheduler">
          <h2 className="text-lg font-bold flex items-center gap-2"><Clock className="w-4 h-4 text-sky-400" /> Scheduler</h2>
          {loading || !scheduler ? (
            <div className="mt-4 text-sm text-slate-500">Loading…</div>
          ) : (
            <div className="mt-4 space-y-3">
              <KV label="Tick scheduled" value={scheduler.scheduled ? 'yes' : 'no'} testid="admin-engine27-sched-scheduled" tone={scheduler.scheduled ? 'good' : 'bad'} />
              <KV label="Interval" value={`${scheduler.interval_seconds}s`} testid="admin-engine27-sched-interval" />
              <KV label="Next tick" value={nextRun} testid="admin-engine27-sched-next" />
              <KV label="Demo events" value={scheduler.demo_events_enabled ? 'on' : 'off'} testid="admin-engine27-sched-demo" />
              <KV label="Scheduler" value={scheduler.scheduler_running ? 'running' : 'stopped'} testid="admin-engine27-sched-running" tone={scheduler.scheduler_running ? 'good' : 'bad'} />
            </div>
          )}

          {/* INGESTION SOURCES (v2 — real provider API ingestion) */}
          <h3 className="text-sm font-bold mt-6 pt-5 border-t border-white/5 flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-emerald-400" /> Live ingestion sources
          </h3>
          {!ingestion ? (
            <div className="mt-3 text-xs text-slate-500">Loading…</div>
          ) : ingestion.total_live_sources === 0 ? (
            <div className="mt-3 text-xs text-slate-400 leading-relaxed" data-testid="admin-engine27-ingestion-empty">
              No live HTTP sources configured. Prices come from the static catalog with ±3% jitter.
              <br />
              <span className="text-slate-500">
                To plug one in, set <code className="text-emerald-300 bg-slate-900 px-1 py-0.5 rounded text-[10px]">ENGINE27_PROVIDER_GROQ_PRICING_URL</code> (or any other provider) and restart.
              </span>
            </div>
          ) : (
            <ul className="mt-3 space-y-2" data-testid="admin-engine27-ingestion-list">
              {ingestion.sources.map((s) => (
                <li key={s.name} className="text-xs flex items-center justify-between gap-2 py-1.5 border-b border-white/5" data-testid={`admin-engine27-source-${s.name}`}>
                  <span className={`font-mono ${s.is_fallback ? 'text-slate-500' : s.ok ? 'text-emerald-300' : 'text-rose-300'}`}>
                    {s.name}
                  </span>
                  <span className="text-slate-400">
                    {s.is_fallback ? 'fallback' : s.ok ? `${s.rows} rows` : (s.error || 'unreachable')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* RECENT ALERTS */}
        <section className="lg:col-span-3 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="admin-engine27-alerts">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold flex items-center gap-2"><Bell className="w-4 h-4 text-amber-400" /> Recent alerts</h2>
            <div className="flex items-center gap-2">
              {isPro && (
                <div className="flex bg-white/5 rounded-md p-0.5 text-xs" data-testid="admin-engine27-scope-tabs">
                  <button
                    onClick={() => setScope('org')}
                    className={`px-2 py-1 rounded ${scope === 'org' ? 'bg-emerald-500 text-slate-950 font-bold' : 'text-slate-400'}`}
                    data-testid="admin-engine27-scope-org"
                  >
                    My org
                  </button>
                  <button
                    onClick={() => setScope('all')}
                    className={`px-2 py-1 rounded ${scope === 'all' ? 'bg-emerald-500 text-slate-950 font-bold' : 'text-slate-400'}`}
                    data-testid="admin-engine27-scope-all"
                  >
                    Platform
                  </button>
                </div>
              )}
              <span className="text-xs text-slate-400 font-mono">{alerts.length} shown</span>
            </div>
          </div>
          {loading ? (
            <div className="mt-4 text-sm text-slate-500">Loading…</div>
          ) : alerts.length === 0 ? (
            <div className="mt-4 text-sm text-slate-500 italic" data-testid="admin-engine27-alerts-empty">
              No alerts dispatched yet. Trigger one with “Send test alert” above, or wait for the next ≥{threshold}% spot burst.
            </div>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-400 font-mono uppercase tracking-wider border-b border-white/5">
                    <th className="py-2 pr-3">Provider</th>
                    <th className="py-2 pr-3">Model</th>
                    <th className="py-2 pr-3">Drop</th>
                    {scope === 'all' && <th className="py-2 pr-3">Org</th>}
                    <th className="py-2 pr-3">Dispatched at</th>
                    <th className="py-2 pr-3">Webhook</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((a) => (
                    <tr key={(a.event_id || '') + (a.dispatched_at || a.alerted_at)} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid="admin-engine27-alert-row">
                      <td className="py-2 pr-3 text-emerald-300 font-mono text-xs">{a.provider_id}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{a.model}</td>
                      <td className="py-2 pr-3"><span className="text-amber-300 font-bold flex items-center gap-1"><TrendingDown className="w-3 h-3" />−{a.price_drop_pct}%</span></td>
                      {scope === 'all' && <td className="py-2 pr-3 text-slate-400 font-mono text-xs">{a.org_id || 'global'}</td>}
                      <td className="py-2 pr-3 text-slate-400 font-mono text-xs">{new Date(a.dispatched_at || a.alerted_at).toLocaleString()}</td>
                      <td className="py-2 pr-3 text-slate-500 text-xs">{a.webhook_url_used ? 'custom' : 'default'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function KV({ label, value, testid, tone }) {
  const valueClass = tone === 'good' ? 'text-emerald-300' : tone === 'bad' ? 'text-rose-300' : 'text-white';
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className="text-xs text-slate-400">{label}</span>
      <span className={`text-sm font-mono ${valueClass}`}>{value}</span>
    </div>
  );
}
